import { addDays, formatISO, parseISO, subYears, addYears } from 'date-fns';
import type { CalendarItem } from '@/hooks/useCalendarItems';
import { fetchGoogleUserEmail, GOOGLE_SCOPES, isGoogleConfigured, requestGoogleAccessToken } from '@/lib/google-auth';

const GOOGLE_CALENDAR_NAME = 'Adulting LifeOS';
const GOOGLE_CALENDAR_DESCRIPTION = 'Managed by Adulting to keep your key dates visible across your devices.';
const GOOGLE_CALENDAR_REQUEST_TIMEOUT_MS = 20000;
const GOOGLE_CALENDAR_SYNC_CONCURRENCY = 6;

type GoogleCalendarEvent = {
  id: string;
  extendedProperties?: {
    private?: Record<string, string>;
  };
};

function calendarUrl(calendarId: string) {
  return `https://calendar.google.com/calendar/u/0/r?cid=${encodeURIComponent(calendarId)}`;
}

function toDateOnly(date: Date) {
  return formatISO(date, { representation: 'date' });
}

async function authorizedCalendarRequest(path: string, init?: RequestInit) {
  const accessToken = await requestGoogleAccessToken([
    GOOGLE_SCOPES.calendarAppCreated,
    GOOGLE_SCOPES.userInfoEmail,
  ]);

  return authorizedCalendarRequestWithToken(accessToken, path, init);
}

async function authorizedCalendarRequestWithToken(accessToken: string, path: string, init?: RequestInit) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), GOOGLE_CALENDAR_REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`https://www.googleapis.com/calendar/v3${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
      },
    });

    return { accessToken, response };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Google Calendar request timed out.');
    }

    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

function buildEventBody(item: CalendarItem) {
  return {
    summary: `${item.emoji} ${item.title}`,
    description: [
      `${item.sectionLabel}${item.subtitle ? ` · ${item.subtitle}` : ''}`,
      '',
      `From Adulting: ${window.location.origin}${item.path}`,
    ].join('\n'),
    start: { date: item.date },
    end: { date: toDateOnly(addDays(parseISO(item.date), 1)) },
    extendedProperties: {
      private: {
        adultingManaged: 'true',
        adultingItemId: item.id,
        adultingSource: item.source,
      },
    },
  };
}

export function isGoogleCalendarConfigured(): boolean {
  return isGoogleConfigured();
}

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>,
) {
  const queue = [...items];
  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length > 0) {
      const next = queue.shift();
      if (!next) return;
      await worker(next);
    }
  });

  await Promise.all(workers);
}

export async function ensureAdultingGoogleCalendar(existingCalendarId?: string | null) {
  const accessToken = await requestGoogleAccessToken([
    GOOGLE_SCOPES.calendarAppCreated,
    GOOGLE_SCOPES.userInfoEmail,
  ]);
  const userEmail = await fetchGoogleUserEmail(accessToken);

  if (existingCalendarId) {
    const { response } = await authorizedCalendarRequestWithToken(
      accessToken,
      `/calendars/${encodeURIComponent(existingCalendarId)}`,
    );
    if (response.ok) {
      const data = await response.json();
      return {
        accessToken,
        userEmail,
        calendarId: data.id as string,
        calendarName: (data.summary as string) || GOOGLE_CALENDAR_NAME,
        calendarUrl: calendarUrl(data.id as string),
      };
    }
  }

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London';
  const { response: createResponse } = await authorizedCalendarRequestWithToken(accessToken, '/calendars', {
    method: 'POST',
    body: JSON.stringify({
      summary: GOOGLE_CALENDAR_NAME,
      description: GOOGLE_CALENDAR_DESCRIPTION,
      timeZone: timezone,
    }),
  });

  if (!createResponse.ok) {
    const message = await createResponse.text();
    throw new Error(`Could not create your Adulting Google calendar: ${message}`);
  }

  const created = await createResponse.json();
  return {
    accessToken,
    userEmail,
    calendarId: created.id as string,
    calendarName: (created.summary as string) || GOOGLE_CALENDAR_NAME,
    calendarUrl: calendarUrl(created.id as string),
  };
}

export async function syncAdultingCalendarItems(items: CalendarItem[], existingCalendarId?: string | null) {
  const ensured = await ensureAdultingGoogleCalendar(existingCalendarId);
  const { calendarId, accessToken } = ensured;

  const timeMin = `${toDateOnly(subYears(new Date(), 1))}T00:00:00Z`;
  const timeMax = `${toDateOnly(addYears(new Date(), 5))}T23:59:59Z`;
  const { response: listResponse } = await authorizedCalendarRequestWithToken(
    accessToken,
    `/calendars/${encodeURIComponent(calendarId)}/events?maxResults=2500&privateExtendedProperty=adultingManaged%3Dtrue&singleEvents=false&showDeleted=false&timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}`,
  );

  if (!listResponse.ok) {
    const message = await listResponse.text();
    throw new Error(`Could not read your Adulting Google calendar events: ${message}`);
  }

  const existingEvents = ((await listResponse.json()).items || []) as GoogleCalendarEvent[];
  const existingByItemId = new Map<string, GoogleCalendarEvent>();

  existingEvents.forEach((event) => {
    const adultingItemId = event.extendedProperties?.private?.adultingItemId;
    if (adultingItemId) {
      existingByItemId.set(adultingItemId, event);
    }
  });

  const activeItemIds = new Set(items.map((item) => item.id));
  let createdCount = 0;
  let updatedCount = 0;
  let deletedCount = 0;

  await runWithConcurrency(items, GOOGLE_CALENDAR_SYNC_CONCURRENCY, async (item) => {
    const existingEvent = existingByItemId.get(item.id);
    const eventBody = buildEventBody(item);

    if (existingEvent) {
      const { response } = await authorizedCalendarRequestWithToken(
        accessToken,
        `/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(existingEvent.id)}`,
        {
          method: 'PATCH',
          body: JSON.stringify(eventBody),
        },
      );

      if (!response.ok) {
        const message = await response.text();
        throw new Error(`Could not update a Google Calendar event: ${message}`);
      }

      updatedCount += 1;
      return;
    }

    const { response } = await authorizedCalendarRequestWithToken(
      accessToken,
      `/calendars/${encodeURIComponent(calendarId)}/events`,
      {
        method: 'POST',
        body: JSON.stringify(eventBody),
      },
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Could not create a Google Calendar event: ${message}`);
    }

    createdCount += 1;
  });

  const eventsToDelete = existingEvents.filter((event) => {
    const adultingItemId = event.extendedProperties?.private?.adultingItemId;
    return Boolean(adultingItemId) && !activeItemIds.has(adultingItemId);
  });

  await runWithConcurrency(eventsToDelete, GOOGLE_CALENDAR_SYNC_CONCURRENCY, async (event) => {
    const adultingItemId = event.extendedProperties?.private?.adultingItemId;
    if (!adultingItemId) return;

    const { response } = await authorizedCalendarRequestWithToken(
      accessToken,
      `/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(event.id)}`,
      {
        method: 'DELETE',
      },
    );

    if (!response.ok && response.status !== 410) {
      const message = await response.text();
      throw new Error(`Could not remove an old Google Calendar event: ${message}`);
    }

    deletedCount += 1;
  });

  return {
    ...ensured,
    createdCount,
    updatedCount,
    deletedCount,
  };
}

export async function upsertAdultingCalendarItem(item: CalendarItem, existingCalendarId?: string | null) {
  const ensured = await ensureAdultingGoogleCalendar(existingCalendarId);
  const { calendarId, accessToken } = ensured;
  const timeMin = `${toDateOnly(subYears(parseISO(item.date), 1))}T00:00:00Z`;
  const timeMax = `${toDateOnly(addYears(parseISO(item.date), 1))}T23:59:59Z`;
  const query = [
    `maxResults=5`,
    `singleEvents=false`,
    `showDeleted=false`,
    `timeMin=${encodeURIComponent(timeMin)}`,
    `timeMax=${encodeURIComponent(timeMax)}`,
    `privateExtendedProperty=${encodeURIComponent('adultingManaged=true')}`,
    `privateExtendedProperty=${encodeURIComponent(`adultingItemId=${item.id}`)}`,
  ].join('&');

  const { response: listResponse } = await authorizedCalendarRequestWithToken(
    accessToken,
    `/calendars/${encodeURIComponent(calendarId)}/events?${query}`,
  );

  if (!listResponse.ok) {
    const message = await listResponse.text();
    throw new Error(`Could not look up the Google Calendar event for this reminder: ${message}`);
  }

  const existingEvent = (((await listResponse.json()).items || []) as GoogleCalendarEvent[])[0];
  const eventBody = buildEventBody(item);

  if (existingEvent) {
    const { response } = await authorizedCalendarRequestWithToken(
      accessToken,
      `/calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(existingEvent.id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(eventBody),
      },
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(`Could not update this Google Calendar reminder: ${message}`);
    }

    return {
      ...ensured,
      action: 'updated' as const,
    };
  }

  const { response } = await authorizedCalendarRequestWithToken(
    accessToken,
    `/calendars/${encodeURIComponent(calendarId)}/events`,
    {
      method: 'POST',
      body: JSON.stringify(eventBody),
    },
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Could not create this Google Calendar reminder: ${message}`);
  }

  return {
    ...ensured,
    action: 'created' as const,
  };
}
